<template>
  <div class="phone-block" :class="{ 'phone-block--error': errors.length }">
    <div class="phone-block__wrapper">
      <div class="phone-wrapper">
        <label v-if="label" class="phone-wrapper__label" :for="name">
          {{ label }}
        </label>
        <div
          class="phone bg-white"
          :class="[
            { 'phone--focused': isFocused || isMenuVisible },
            { 'phone--error': errors.length },
            { 'phone--disabled': disabled },
          ]"
        >
          <UIPhoneCodeButton
            ref="button"
            :menu-visible="isMenuVisible"
            :disabled="disabled"
            :selected-country="selectedCountry"
            @open="handlePhoneCodeButtonClick"
          />
          <ClientOnly>
            <UIBaseTransition>
              <UIMenu
                v-if="isMenuVisible"
                ref="menu"
                :model-value="inputValue?.country?.phone_code"
                :searchable="true"
                :search-key="['phone_code', 'name']"
                :items="countyCodes"
                :menu-visible="isMenuVisible"
                :menu-width="computedMenuWidth"
                value-key="phone_code"
                :style="floatingStyles"
              >
                <template #default="{ item, active }">
                  <UIMenuOption :key="item.id" country :active :item @select="handleOptionSelect" />
                </template>
              </UIMenu>
            </UIBaseTransition>
          </ClientOnly>
          <input
            ref="input"
            :value="inputValue?.number"
            class="phone__input"
            :name="name"
            type="text"
            :placeholder="placeholder"
            @focus="handleFocus"
            @blur="handleBlurEvent"
            @input="handleNumberInput"
          />
        </div>
      </div>
    </div>
    <UIBaseTransition>
      <span v-if="errorMessage?.trim()" class="text-error-100"> {{ $t(errorMessage) }}</span>
    </UIBaseTransition>
    <UIBaseTransition>
      <div v-if="displayError && errors.length" class="phone__error">{{ $t(errors[0]) }}</div>
    </UIBaseTransition>
  </div>
</template>

<script setup lang="ts">
import { useFloating, autoUpdate, flip } from '@floating-ui/vue'
import { useField } from 'vee-validate'
import { MaskInput } from 'maska'
import * as countryCodes from 'country-codes-list'

import type { UIInputText, UIPhoneCodeButton, UIMenu } from '#build/components'
import { type MenuProps, type Phone, type PhoneCountryOption } from '~/types/ui'
import set from 'lodash/set'

const props = defineProps<
  {
    name: string
    label?: string
    placeholder: string
    phone?: number
    disabled?: boolean
    displayError?: boolean
  } & Omit<MenuProps, 'menuVisible' | 'valueKey'>
>()

const emit = defineEmits<{
  (e: 'blur', event: FocusEvent): void
}>()

const button = ref<InstanceType<typeof UIPhoneCodeButton> | null>(null)
const codeInput = ref<InstanceType<typeof UIInputText> | null>(null)
const menu = ref<InstanceType<typeof UIMenu> | null>(null)

const countyCodes = computed<PhoneCountryOption[] | []>(() => {
  return (
    countryCodes?.all()?.map(({ countryNameEn, countryCallingCode, countryCode }) => ({
      name: countryNameEn,
      phone_code: `+${countryCallingCode}`,
      iso2: countryCode,
      id: 0,
    })) || []
  )
})

const selectedCountry = computed(() => {
  return countyCodes.value.find(({ iso2 }) => iso2 === inputValue.value?.country?.iso2)
})

const input = ref<HTMLInputElement | null>(null)

const handleOptionSelect = (country: PhoneCountryOption) => {
  isMenuVisible.value = false

  inputValue.value = {
    ...inputValue.value,
    country,
  }

  if (input.value) {
    input.value.focus()
  }
}

const name = toRef(props, 'name')

const {
  value: inputValue,
  setValue,
  errorMessage,
  handleBlur,
  errors,
  setTouched,
  setErrors,
} = useField<Partial<Phone> | undefined>(name, undefined, {
  initialValue: props.modelValue,
})

const handleNumberInput = (event: Event) => {
  const value = parseFloat((event.target as HTMLInputElement).value)

  if (!value) {
    inputValue.value = {
      ...inputValue.value,
      number: undefined,
    }
    return
  }

  inputValue.value = {
    ...inputValue.value,
    number: value,
  }
}

const isFocused = ref(false)

const handleFocus = () => {
  isFocused.value = true
}

const handleBlurEvent = (event: FocusEvent) => {
  handleBlur(event)
  isFocused.value = false

  emit('blur', event)
}

const isMenuVisible = ref(false)

const { isMobile } = useAppBreakpoints()

const target = computed(() => button.value?.$el)

onClickOutside(
  menu,
  () => {
    isMenuVisible.value = false
  },
  { ignore: [target] }
)

const { floatingStyles } = useFloating(target, menu, {
  placement: 'bottom-start',
  whileElementsMounted: autoUpdate,
  strategy: 'fixed',
  middleware: [
    flip({
      fallbackPlacements: ['bottom-start', 'top-start'],
    }),
  ],
  open: isMenuVisible,
})

const handlePhoneCodeButtonClick = () => {
  isMenuVisible.value = !isMenuVisible.value

  nextTick(() => {
    menu.value?.searchRef?.input?.input?.focus()
  })
}

const computedMenuWidth = computed(() => {
  if (isMobile) {
    return '80%'
  }

  return props.menuWidth
})

const isPhoneCodeFocused = ref(false)

const handlePhoneCodeFocus = (event: FocusEvent) => {
  isPhoneCodeFocused.value = true

  const value = (event.target as HTMLInputElement).value

  handlePhoneCodeInput(value)
}

const handlePhoneCodeBlur = (event: FocusEvent) => {
  isPhoneCodeFocused.value = false

  const value = (event.target as HTMLInputElement).value

  const country = countyCodes.value.find(({ phone_code: phoneCode }) => phoneCode === value)

  if (country) {
    handleOptionSelect(country)
  } else {
    inputValue.value = {
      ...inputValue.value,
      country: {
        iso2: '',
        name: '',
        id: 0,
        phone_code: '',
      },
    }
  }
}

const handlePhoneCodeInput = (value: string) => {
  isMenuVisible.value = value.length >= 2 && isPhoneCodeFocused.value

  setValue({
    ...inputValue.value,
    // @ts-ignore
    country: {
      ...inputValue.value?.country,
      phone_code: value,
    },
  })
}

const maskInput = ref<MaskInput | null>(null)

const { detectCountry } = useDetectCountry()

onMounted(async () => {
  if (input.value) {
    maskInput.value = new MaskInput(input.value, {
      mask: '#'.repeat(16),
    })
  }

  if (!inputValue.value?.country?.iso2) {
    const countryDetected = await detectCountry()
    if (countryDetected) {
      const country = countryCodes.all().find((country) => country.countryCode === countryDetected)
      if (!country) return

      inputValue.value = {
        ...inputValue.value,
        countryCode: `+${country.countryCallingCode}`,
        number: undefined,
      }
      inputValue.value.country = {
        iso2: country.countryCode,
        name: country.countryNameEn,
        id: 0,
        phone_code: `+${country.countryCallingCode}`,
      }
      setValue(inputValue.value)
      setTimeout(() => {
        setTouched(false)
        setErrors([])
      }, 200)
    }
  }
})
</script>

<style scoped lang="scss">
@use '@/styles/variables.scss' as *;

.phone {
  padding: 12px 16px 12px 8px;
  border-radius: $base-border-radius;
  border: $base-border-width solid $black-20;
  box-shadow: inset -8px -8px 16px 0px rgba(237, 164, 54, 0.08);
  height: 48px;
  display: flex;
  align-items: center;
  transition: $base-transition border;
  position: relative;

  &-block {
    &__wrapper {
      display: flex;
      width: 100%;
      gap: 8px;
      align-items: end;
    }

    &__code-input {
      display: none;
      max-width: 80px;
    }

    &--error {
      .phone {
        &-wrapper__label {
          color: $error-100;
        }

        &__input {
          &::placeholder {
            color: $error-100;
          }
        }

        &--error {
          border: $base-border-width solid $error-100;
        }

        &-block__code-input {
          :deep() {
            .input-text {
              &__label {
                color: $error-100;
              }

              &__input {
                border: $base-border-width solid $error-100;
              }
            }
          }
        }
      }
    }

    @media screen and (max-width: 767px) {
      .phone-block__code-input {
        display: block;

        :deep() {
          .input-text {
            &__input {
              padding: 8px 16px;
            }
          }
        }
      }
      .phone-wrapper__label {
        margin-right: 80px;
      }
    }
  }

  @media screen and (max-width: 767px) {
    padding: 8px 16px;
    position: static;
  }

  &-wrapper {
    font-size: 14px;
    line-height: 140%;
    flex-grow: 1;
    color: $black-80;

    &__label {
      display: block;
      margin-bottom: 2px;
      margin-left: 16px;
      font-weight: 500;

      @media screen and (max-width: 767px) {
        span {
          display: none;
        }
      }
    }
  }

  &:hover {
    border: $base-border-width solid $black-60;
  }

  &--focused {
    border: $base-border-width solid $black-100 !important;
  }

  &--disabled {
    background: $black-05;
    border: $base-border-width solid $black-20 !important;
    color: $black-60;
    pointer-events: none;

    input {
      background: $black-05;
      color: $black-60;
    }
  }

  &__error {
    color: $error-100;
    margin-top: 2px;
    display: block;
    margin-left: 16px;
    font-size: 12px;
    line-height: 140%;
  }

  &__input {
    margin-left: 8px !important;
    width: 100%;
    border: 0;
    outline: none;
    font-size: 14px;

    &::placeholder {
      color: $black-40;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    @media screen and (max-width: 767px) {
      margin-left: 0;
      font-size: 16px;
    }
  }
}
</style>
